import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, CheckCircle2, CircleX } from 'lucide-react';
import { useState } from 'react';
import { Checkbox, ErrorBanner, Field, Textarea } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { formatAmount, formatDate } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useCloseYear, type CloseCheck, type CloseYear } from '@/lib/periods';
import { cn } from '@/lib/utils';
import { useAccountNames } from '@/lib/accounts';

export const Route = createFileRoute('/accounting/periods/$fiscalYearId/close')({
  component: CloseYearWizard,
});

const STEPS = [
  'accountingPages.periodClose.stepCheck',
  'accountingPages.periodClose.stepReview',
  'accountingPages.periodClose.stepClose',
] as const;

/**
 * Year-end close wizard (document 06 A5; document 04 "Periods and close"): checklist → net
 * profit and the retained-earnings roll → close (posts the year-end entry, adds the next year).
 */
function CloseYearWizard() {
  const t = useT();
  const { fiscalYearId } = Route.useParams();
  const navigate = useNavigate();
  const orgId = useOrgId();
  const close = useCloseYear(fiscalYearId);
  const [step, setStep] = useState(0);
  const back = () => void navigate({ to: '/accounting/periods' });
  useHotkeys({ 'ctrl+alt+x': back });

  if (!orgId) return <EmptyState title={t('shell.selectClient')} />;
  if (close.isPending)
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (close.isError)
    return (
      <EmptyState title={t('accountingPages.periodClose.loadError')} hint={close.error.message} />
    );
  const v = close.data;
  const fy = v.fiscalYear;

  return (
    <OverlayPage
      title={t('accountingPages.periodClose.title', { label: fy.label })}
      subtitle={`${formatDate(fy.startDate)} – ${formatDate(fy.endDate)}`}
      onClose={back}
      footerLeft={
        <Button variant="outline" onClick={back}>
          {fy.status === 'closed'
            ? t('accountingPages.periodClose.backToPeriods')
            : t('common.cancel')}
        </Button>
      }
      footerRight={
        fy.status === 'closed' ? (
          <ReopenButton view={v} />
        ) : (
          <>
            {step > 0 ? (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
                {t('accountingPages.periodClose.back')}
              </Button>
            ) : null}
            {step < STEPS.length - 1 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={step === 0 && v.checks.some((c) => c.status === 'blocking')}
              >
                {t('accountingPages.periodClose.next')}
              </Button>
            ) : null}
          </>
        )
      }
    >
      <div className="mx-auto flex max-w-[900px] flex-col gap-6">
        {fy.status === 'closed' ? (
          <ClosedSummary view={v} />
        ) : (
          <>
            <ol
              className="flex flex-wrap gap-2"
              aria-label={t('accountingPages.periodClose.stepsAria')}
            >
              {STEPS.map((s, i) => (
                <li
                  key={s}
                  aria-current={i === step ? 'step' : undefined}
                  className={cn(
                    'flex items-center gap-2 rounded-full border px-3 py-1 text-sm',
                    i === step
                      ? 'border-primary bg-primary/5 font-medium text-primary'
                      : 'text-muted-foreground',
                  )}
                >
                  <span className="tnum">{i + 1}</span> {t(s)}
                </li>
              ))}
            </ol>
            {step === 0 ? <Checklist view={v} /> : null}
            {step === 1 ? <ClosingEntry view={v} /> : null}
            {step === 2 ? <Confirm view={v} /> : null}
          </>
        )}
      </div>
    </OverlayPage>
  );
}

function StatusIcon({ status }: { status: CloseCheck['status'] }) {
  const t = useT();
  if (status === 'ok')
    return (
      <CheckCircle2
        className="size-5 text-success"
        aria-label={t('accountingPages.periodClose.iconDone')}
      />
    );
  if (status === 'warning')
    return (
      <AlertTriangle
        className="size-5 text-warning"
        aria-label={t('accountingPages.periodClose.iconWarning')}
      />
    );
  return (
    <CircleX
      className="size-5 text-destructive"
      aria-label={t('accountingPages.periodClose.iconBlocking')}
    />
  );
}

function Checklist({ view: v }: { view: CloseYear }) {
  const t = useT();
  return (
    <section
      className="rounded-lg border bg-card"
      aria-label={t('accountingPages.periodClose.checklistAria')}
    >
      <ul>
        {v.checks.map((c) => (
          <li
            key={c.key}
            className="flex gap-3 border-b px-4 py-3 last:border-0"
            data-testid={`check-${c.key}`}
          >
            <span className="mt-0.5">
              <StatusIcon status={c.status} />
            </span>
            <div className="flex-1 text-sm">
              <div className="font-medium">{c.label}</div>
              <div className="text-muted-foreground">{c.detail}</div>
              {c.items?.length ? (
                <ul className="mt-1 list-disc ps-4 text-muted-foreground">
                  {c.items.map((i) => (
                    <li key={i} className="tnum">
                      {i}
                    </li>
                  ))}
                </ul>
              ) : null}
              {c.key === 'months_locked' &&
              c.status === 'blocking' &&
              v.fiscalYear.status !== 'closed' ? (
                <LockRemaining view={v} />
              ) : null}
            </div>
            <CheckLink check={c} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function CheckLink({ check: c }: { check: CloseCheck }) {
  const t = useT();
  if (!c.link || c.status === 'ok') return null;
  if (c.link.startsWith('/reports/trial-balance'))
    return (
      <Link
        to="/reports/$reportKey"
        params={{ reportKey: 'trial-balance' }}
        search={{ to: new URLSearchParams(c.link.split('?')[1] ?? '').get('to') ?? undefined }}
        className="text-sm text-primary hover:underline"
      >
        {t('accountingPages.periodClose.open')}
      </Link>
    );
  if (c.link === '/accounting/chart-of-accounts')
    return (
      <Link to="/accounting/chart-of-accounts" className="text-sm text-primary hover:underline">
        {t('accountingPages.periodClose.review')}
      </Link>
    );
  return null;
}

/** The first step can lock what is still open, with the same reason rule as the Periods page. */
function LockRemaining({ view: v }: { view: CloseYear }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [reason, setReason] = useState(
    t('accountingPages.periodClose.defaultLockReason', { label: v.fiscalYear.label }),
  );
  const [confirmWarnings, setConfirmWarnings] = useState(false);
  const m = useMutation({
    mutationFn: () =>
      api(`/orgs/${orgId}/periods/lock`, {
        method: 'POST',
        body: JSON.stringify({
          through: v.fiscalYear.endDate,
          reason: reason.trim(),
          confirmWarnings,
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['periods', orgId] });
      toast(
        t('accountingPages.periodClose.toastLocked', {
          date: ltr(formatDate(v.fiscalYear.endDate)),
        }),
      );
    },
  });
  if (!v.fiscalYear.periods.length) return null;
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
      <Field label={t('accountingPages.periodClose.reasonForLocking')}>
        {(id) => (
          <Textarea
            id={id}
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </Field>
      {m.error ? (
        <p role="alert" className="text-destructive">
          {m.error.message}
        </p>
      ) : null}
      {m.error && 'code' in m.error && m.error.code === 'PERIOD_WARNINGS' ? (
        <Checkbox
          label={t('accountingPages.periodClose.lockAnyway')}
          checked={confirmWarnings}
          onChange={(e) => setConfirmWarnings(e.target.checked)}
        />
      ) : null}
      <div>
        <Button
          size="sm"
          disabled={reason.trim().length < 3 || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending
            ? t('accountingPages.periodClose.locking')
            : t('accountingPages.periodClose.lockThrough', {
                date: formatDate(v.fiscalYear.endDate),
              })}
        </Button>
      </div>
    </div>
  );
}

function NetProfit({ view: v }: { view: CloseYear }) {
  const t = useT();
  const loss = v.netProfit.startsWith('-');
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {loss
          ? t('accountingPages.periodClose.netLoss')
          : t('accountingPages.periodClose.netProfit')}
      </div>
      <div className="tnum text-3xl font-semibold" data-testid="net-profit">
        {v.baseCurrency} {formatAmount(v.netProfit)}
      </div>
    </div>
  );
}

function ClosingEntry({ view: v }: { view: CloseYear }) {
  const t = useT();
  const names = useAccountNames();
  const explain = {
    date: formatDate(v.fiscalYear.endDate),
    account: v.retainedEarnings
      ? `${v.retainedEarnings.code} ${names.name(v.retainedEarnings.accountId, v.retainedEarnings.name)}`
      : t('accountingPages.periodClose.retainedEarnings'),
  };
  const totalDebit = v.closeLines.reduce((s, l) => s + (toMinor(l.debit ?? '') ?? 0n), 0n);
  const totalCredit = v.closeLines.reduce((s, l) => s + (toMinor(l.credit ?? '') ?? 0n), 0n);
  return (
    <div className="flex flex-col gap-4">
      <NetProfit view={v} />
      {v.closeLines.length ? (
        <>
          <p className="text-sm text-muted-foreground">
            {v.netProfit.startsWith('-')
              ? t('accountingPages.periodClose.explainLoss', explain)
              : t('accountingPages.periodClose.explainProfit', explain)}
          </p>
          <div className="rounded-lg border bg-card">
            <table
              className="w-full text-sm"
              aria-label={t('accountingPages.periodClose.closingEntryAria')}
            >
              <thead className="border-b bg-muted/40 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('accountingPages.periodClose.account')}</th>
                  <th className="w-[160px] px-3 py-2 text-end">
                    {t('accountingPages.periodClose.debits')}
                  </th>
                  <th className="w-[160px] px-3 py-2 text-end">
                    {t('accountingPages.periodClose.credits')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {v.closeLines.map((l) => (
                  <tr key={l.accountId} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      {l.accountCode} {names.name(l.accountId, l.accountName)}
                    </td>
                    <td className="tnum px-3 py-2 text-end">{formatAmount(l.debit)}</td>
                    <td className="tnum px-3 py-2 text-end">{formatAmount(l.credit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-muted/40 font-medium">
                <tr>
                  <td className="px-3 py-2 text-end">{t('accountingPages.periodClose.total')}</td>
                  <td className="tnum px-3 py-2 text-end">{formatMinor(totalDebit)}</td>
                  <td className="tnum px-3 py-2 text-end">{formatMinor(totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('accountingPages.periodClose.noClosingEntry')}
        </p>
      )}
    </div>
  );
}

function Confirm({ view: v }: { view: CloseYear }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const warnings = v.checks.filter((c) => c.status === 'warning');
  const m = useMutation({
    mutationFn: () =>
      api<CloseYear>(`/orgs/${orgId}/fiscal-years/${v.fiscalYear.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ confirmWarnings: confirmed }),
      }),
    onSuccess: async (after) => {
      qc.setQueryData(['periods', orgId, 'close', v.fiscalYear.id], after);
      await qc.invalidateQueries();
      toast(t('accountingPages.periodClose.toastClosed', { label: v.fiscalYear.label }));
    },
  });
  return (
    <div className="flex flex-col gap-4">
      <NetProfit view={v} />
      <ul className="list-disc ps-5 text-sm">
        {v.closeLines.length ? (
          <li>
            {t('accountingPages.periodClose.postEntry', {
              date: formatDate(v.fiscalYear.endDate),
              amount: ltr(`${v.baseCurrency} ${formatAmount(v.netProfit)}`),
            })}
          </li>
        ) : null}
        <li>{t('accountingPages.periodClose.markClosed', { label: v.fiscalYear.label })}</li>
        <li>
          {v.nextYear.exists
            ? t('accountingPages.periodClose.nextExists', {
                date: formatDate(v.nextYear.startDate),
              })
            : t('accountingPages.periodClose.addNext', {
                from: formatDate(v.nextYear.startDate),
                to: formatDate(v.nextYear.endDate),
              })}
        </li>
      </ul>
      {warnings.length ? (
        <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-sm">
          <p className="font-medium">{t('accountingPages.periodClose.warnings')}</p>
          <ul className="mt-1 list-disc ps-4">
            {warnings.map((w) => (
              <li key={w.key}>
                {w.label}: {w.detail}
              </li>
            ))}
          </ul>
          <Checkbox
            className="mt-2"
            label={t('accountingPages.periodClose.reviewedWarnings')}
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
        </div>
      ) : null}
      {m.error ? <ErrorBanner>{m.error.message}</ErrorBanner> : null}
      {!v.canClose ? (
        <ErrorBanner>
          {v.checks.some((c) => c.status === 'blocking')
            ? t('accountingPages.periodClose.fixFirst')
            : t('accountingPages.periodClose.needsRights')}
        </ErrorBanner>
      ) : null}
      <div>
        <Button
          disabled={!v.canClose || (warnings.length > 0 && !confirmed) || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending
            ? t('accountingPages.periodClose.closing')
            : t('accountingPages.periodClose.title', { label: v.fiscalYear.label })}
        </Button>
      </div>
    </div>
  );
}

function ClosedSummary({ view: v }: { view: CloseYear }) {
  const t = useT();
  const fy = v.fiscalYear;
  const by = fy.closedByName ?? t('accountingPages.periodClose.anAccountant');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3 text-sm">
        <CheckCircle2 className="mt-0.5 size-5 text-success" aria-hidden />
        <div>
          <div className="font-medium">
            {t('accountingPages.periodClose.closedTitle', { label: fy.label })}
          </div>
          <div className="text-muted-foreground">
            {fy.closedAt
              ? t('accountingPages.periodClose.closedByOn', {
                  name: by,
                  date: formatDate(fy.closedAt),
                })
              : t('accountingPages.periodClose.closedBy', { name: by })}{' '}
            {fy.closeEntryId ? (
              <Link
                to="/accounting/journal-entries/$entryId"
                params={{ entryId: fy.closeEntryId }}
                className="text-primary hover:underline"
              >
                {t('accountingPages.periodClose.openCloseEntry', { no: fy.closeEntryNo })}
              </Link>
            ) : (
              t('accountingPages.periodClose.noProfitOrLoss')
            )}
          </div>
        </div>
      </div>
      <NetProfit view={v} />
    </div>
  );
}

function ReopenButton({ view: v }: { view: CloseYear }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api<CloseYear>(`/orgs/${orgId}/fiscal-years/${v.fiscalYear.id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: async (after) => {
      qc.setQueryData(['periods', orgId, 'close', v.fiscalYear.id], after);
      await qc.invalidateQueries();
      toast(t('accountingPages.periodClose.toastReopened', { label: v.fiscalYear.label }));
      setOpen(false);
      setReason('');
    },
  });
  return (
    <>
      <Button variant="outline" disabled={!v.canManage} onClick={() => setOpen(true)}>
        {t('accountingPages.periodClose.reopenYear')}
      </Button>
      <Dialog open={open} onOpenChange={(o) => (!o ? (setOpen(false), m.reset()) : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('accountingPages.periodClose.reopenTitle', { label: v.fiscalYear.label })}
            </DialogTitle>
            <DialogDescription>
              {t('accountingPages.periodClose.reopenDescription', {
                date: formatDate(v.fiscalYear.endDate),
              })}
            </DialogDescription>
          </DialogHeader>
          <Field
            label={t('accountingPages.periodClose.reason')}
            hint={t('accountingPages.periodClose.reasonHint')}
          >
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          {m.error ? (
            <p role="alert" className="text-sm text-destructive">
              {m.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || m.isPending}
              onClick={() => m.mutate()}
            >
              {m.isPending
                ? t('accountingPages.periodClose.reopening')
                : t('accountingPages.periodClose.reopen')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
